/* eslint-disable max-lines */
import { type BenefitType, type ReferentPriceType } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { Hyperlink } from '@strata/react-hyperlink';
import { DetailLabelValuePair, PatientInfoList } from '@strata/react-label-value-pairs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { formatRateString, ServicingRate } from '@strata/react-servicing-rate';
import { SuccessToast } from '@strata/react-toast-message';
import { isTaskTodo, Worklist, WorklistTimer } from '@strata/react-worklist';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { postAccept, postDecline } from 'api/auctionsAPI';
import { ProductLabel } from 'components/shared/ProductLabel';
import { ProspectiveReferralInlineMessage } from 'components/shared/ProspectiveReferralInlineMessage';
import { UnitPriceGrid } from 'components/shared/UnitPriceGrid';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions, RouteCodes } from 'services/constants';
import { handleError } from 'services/Error';
import { canDeclinePendingReferral } from 'services/referralHelpers';
import { AppRoutes } from 'services/routes';
import { useReturnToWorklistCheck } from 'services/worklistHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { type ReferralValueCase } from 'store/api/performanceApi';
import { useGetPlaceOfServiceMapQuery } from 'store/api/referenceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';
import {
    acceptPendingReferral,
    declinePendingReferral,
} from 'store/wonReferrals/wonReferralListThunks';
import { completeWorklistTask, fetchWorklistTasks } from 'store/worklist/worklistThunks';

const routeCodeSpecificDialogText = (routeCode?: string) => {
    switch (routeCode) {
        case RouteCodes.CONTRACT:
            return {
                body: 'This referral is being routed to your pharmacy as part of a defined payer program that you are separately contracted with the payer for.',
                rateDescription:
                    'This referral is unable to be declined based on your participation in this program and you will be reimbursed at the agreed upon contract rates.',
            };
        case RouteCodes.MEM_PREF:
            return {
                body: 'Congratulations! This member has chosen to be serviced by your pharmacy.',
                rateDescription: 'Due to member preference, this referral must be accepted.',
            };
        case RouteCodes.ACCEPT:
            return {
                body: 'This referral is being routed to your pharmacy as part of a defined payer program that you are separately contracted with the payer for.',
                rateDescription:
                    'This referral is unable to be declined based on your participation in this program and you will be reimbursed at the agreed upon contract rates.',
            };
        case RouteCodes.RND_ROBIN:
            return {
                body: 'This referral is being routed to your pharmacy per health plan guidelines.',
                rateDescription:
                    'You will be reimbursed at the contracted rate, and the referral must be accepted.',
            };
        default:
            return {};
    }
};

const ReasonValues = [
    'Proposed reimbursement rate is below cost.',
    'Do not have access to the medication (e.g. LDD).',
    'Cannot meet performance requirement(s).',
    'Can no longer service patient (e.g. out of area).',
];

interface PendingIntakeDialogProps {
    referralId: string;
    payerId: string;
    selectedReferral: {
        segmentId: string;
        routeCode?: string;
        incentiveTotal?: number;
        valueCases?: ReferralValueCase[];
        offerRate?: string;
        referentPriceType: ReferentPriceType;
        priceSource: string;
        priceIsFrozen: boolean;
        placeOfService: string;
        productServiceOid: string;
        isBrand: boolean;
        product?: string;
        benefit: BenefitType;
    };
    referralDetails: ReferralDetails;
    isOpen?: boolean;
    onClose: () => void;
}

export const PendingIntakeDialog = ({
    isOpen = false,
    referralId,
    payerId,
    selectedReferral,
    referralDetails,
    onClose,
}: PendingIntakeDialogProps) => {
    const dispatch = useAppDispatch();
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);

    const writeAuctions = usePermissionCheck(Permissions.WRITE_AUCTIONS);

    const returnToWorklistCheck = useReturnToWorklistCheck();

    const { segmentId, routeCode, valueCases, incentiveTotal, offerRate } = selectedReferral;

    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();

    const worklistTask = useAppSelector((state) =>
        state.worklist.worklistTasks
            ?.filter((task) => isTaskTodo(task))
            .find(
                (task) =>
                    task.referral_id === referralId &&
                    (task.task_type === Worklist.WorkTaskType.PENDINTAKE ||
                        task.task_type === Worklist.WorkTaskType.ACCEPTANCE),
            ),
    );

    const taskId = worklistTask?.task_id;

    const canDecline = canDeclinePendingReferral(routeCode);

    // dialog text that varies based on type
    const dialogText = {
        dialogTitle: `Accept${canDecline ? '/Decline' : ''} Referral`,
        body: `This referral is pending intake and requires action. Accept${
            canDecline ? ' or decline' : ''
        } the referral below.`,
        rateDescription: `Being a provider in good standing, your rate is ${formatRateString({
            bidDecimal: offerRate,
            referentPriceType: selectedReferral.referentPriceType,
        })}.`,
        ...routeCodeSpecificDialogText(routeCode),
    };

    return (
        <Formik<{ accept: string[]; decline: string[]; reason: string }>
            initialValues={{
                accept: [],
                decline: [],
                reason: '',
            }}
            onSubmit={async ({ accept, reason }, { resetForm }) => {
                const isAccept = accept.length === 2;
                try {
                    if (isAccept) {
                        await postAccept({
                            npi: selectedNpi!,
                            referralId,
                            payerId,
                            segmentId,
                            taskId,
                        });
                        dispatch(acceptPendingReferral(referralId, offerRate));
                    } else {
                        await postDecline({
                            npi: selectedNpi!,
                            referralId,
                            payerId,
                            segmentId,
                            taskId,
                            reason,
                        });
                        dispatch(declinePendingReferral(referralId));
                    }

                    // Update worklist tasks in store
                    await dispatch(fetchWorklistTasks());

                    // Optimistically complete worklist task in case worklist-svc hasn't received the message yet
                    if (taskId) {
                        dispatch(completeWorklistTask(taskId));
                    }

                    SuccessToast({
                        message: `Referral was ${isAccept ? 'accepted' : 'declined'} successfully.`,
                    });

                    onClose();
                    resetForm();

                    returnToWorklistCheck();
                } catch (err) {
                    handleError(
                        `An error occurred ${isAccept ? 'accepting' : 'declining'} the referral.`,
                        err as Error,
                    );
                }
            }}
            // Validate either both renew or both decline options are selected
            validationSchema={Yup.object().shape(
                {
                    accept: Yup.array().when(['decline', 'reason'], {
                        // Not sure why, but decline is briefly set to undefined before being set to []
                        is: (decline?: string[], reason?: string) =>
                            decline?.length === 0 && !reason,
                        then: (schema) => schema.min(2, 'Required to accept.'),
                    }),
                    decline: Yup.array().when('accept', {
                        is: (accept: string[]) => accept.length === 0,
                        then: (schema) => schema.min(1, 'Required to decline.'),
                    }),
                    reason: Yup.string().when('accept', {
                        is: (accept: string[]) => accept.length === 0,
                        then: (schema) => schema.required('Required to decline.'),
                    }),
                },
                [
                    ['accept', 'decline'],
                    ['accept', 'reason'],
                ],
            )}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle={dialogText.dialogTitle}
                    secondaryButtonLabel="Cancel"
                    primaryButtonLabel="Save"
                    isCloseButtonShown
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.handleReset();
                    }}
                    isPrimaryButtonDisabled={
                        !writeAuctions || !(formik.isValid && formik.dirty) || formik.isSubmitting
                    }
                    canEscapeKeyClose
                    width={600}
                    dialogContent={
                        <>
                            <div className="margin__bottom--large">{dialogText.body}</div>
                            <div className="sds_pending-dialog__patient-row margin__bottom--large">
                                <div style={{ width: '25%' }}>
                                    {worklistTask && (
                                        <WorklistTimer
                                            dueDate={worklistTask.due_date}
                                            warningDate={worklistTask.warning_date}
                                            completedDate={worklistTask.completed_date}
                                        />
                                    )}
                                </div>
                                <div style={{ width: '25%' }}>
                                    <DetailLabelValuePair
                                        label="Product"
                                        value={
                                            <ProductLabel
                                                product={selectedReferral.product}
                                                isBrand={selectedReferral.isBrand}
                                            />
                                        }
                                    />
                                </div>
                                <PatientInfoList
                                    label="Patient Attributes"
                                    values={valueCases}
                                    incentiveTotal={incentiveTotal}
                                    iconsOnly
                                    style={{ width: '25%' }}
                                />
                                <DetailLabelValuePair
                                    label="Place Of Service"
                                    value={placeOfServiceMap?.[selectedReferral.placeOfService]}
                                />
                            </div>
                            <div className="sds_pending-dialog__rate-container padding__top--medium padding__bottom--medium">
                                <div className="sds_dialog__section-text sds_pending-dialog__rate-label padding__bottom--small">
                                    Your Rate
                                </div>
                                <div className="sds_pending-dialog__rate-value">
                                    {formatRateString({
                                        bidDecimal: offerRate,
                                        referentPriceType: selectedReferral.referentPriceType,
                                    })}
                                </div>
                            </div>
                            <div className="padding__top--large padding__bottom--large">
                                {dialogText.rateDescription}
                            </div>
                            <ProspectiveReferralInlineMessage
                                referralStart={referralDetails.referralStart}
                                outreachDate={referralDetails.outreachDate}
                            />
                            {selectedReferral.priceIsFrozen && (
                                <div className="padding__bottom--large">
                                    <UnitPriceGrid
                                        priceFrozenMsg="Servicing unit price will be locked at the servicing start date for the life of this referral."
                                        referralDetails={selectedReferral}
                                        priceDate={formatDate(dayjs(), DateFormats.HYPHEN_DATE)}
                                    />
                                </div>
                            )}
                            <div className="sds_dialog__section-text">Accept</div>
                            <CheckboxListFormik
                                name="accept"
                                required
                                disabled={!writeAuctions}
                                options={[
                                    {
                                        value: 'agreeToRate',
                                        component: (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <span className="margin__right--x-small">
                                                    I agree to
                                                </span>
                                                <ServicingRate
                                                    servicingRate={offerRate}
                                                    referentPriceType={
                                                        selectedReferral.referentPriceType
                                                    }
                                                />
                                            </span>
                                        ),
                                    },
                                    {
                                        value: 'agreeToTerms',
                                        component: (
                                            <span>
                                                <span>{'I agree to the '}</span>
                                                <Hyperlink
                                                    linkText="Terms & Conditions"
                                                    id="pending-intake-terms-and-conditions-link"
                                                    navUrl={AppRoutes.termsAndConditions}
                                                />
                                                <span>.</span>
                                            </span>
                                        ),
                                    },
                                ]}
                                onChange={(newValues) => {
                                    void formik.setValues({
                                        accept: newValues,
                                        decline: [],
                                        reason: '',
                                    });
                                }}
                            />
                            {canDecline && (
                                <>
                                    <div className="sds_dialog__section-text">Decline</div>
                                    <CheckboxListFormik
                                        name="decline"
                                        required
                                        disabled={!writeAuctions}
                                        options={[
                                            {
                                                value: 'decline',
                                                text: 'I cannot accept this referral.',
                                            },
                                        ]}
                                        onChange={(newValues) => {
                                            void formik.setValues({
                                                ...formik.values,
                                                accept: [],
                                                decline: newValues,
                                            });
                                        }}
                                    />
                                    <div style={{ width: '65%' }}>
                                        <SuggestSingleSelectFormik
                                            id="reason"
                                            label="Reason"
                                            disabled={!writeAuctions}
                                            values={ReasonValues}
                                            onChange={(newValue) => {
                                                void formik.setValues({
                                                    ...formik.values,
                                                    accept: [],
                                                    reason: newValue!,
                                                });
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
